import {
  DatePicker,
  FormField,
  type IDatePickerProps,
  type IFormFieldProps,
} from "@/ui-kit";
import { useCallback, useId } from "react";
import { Controller, useFormContext, useFormState } from "react-hook-form";
import { get } from "../utils";
import { DateTimeUtils } from "@/utils/DateTimeUtils";

type Props = Omit<IDatePickerProps, "value" | "onChange" | "label"> &
  Partial<IFormFieldProps> & {
    name: string;
    setDisplayValue?: (value: Date) => string;
    formatDate?: (date: Date | undefined) => string;
    onChange?: (value: Date | undefined) => void;
    formElementClass?: string;
    clear?: boolean;
  };

export const DatePickerControl = (props: Props) => {
  const {
    label,
    message,
    required,
    tooltipContent,
    accessory,
    formElementClass,
    name,
    invalid,
    formatDate,
    onChange,
    clear,
    description,
    ...restDatepickerProps
  } = props;
  const { control } = useFormContext();
  const { errors } = useFormState();
  const errorMessage = get(errors, name)?.message as string | undefined;
  const inputId = useId();

  const defaultFormatDate = useCallback((date: Date | undefined): string => {
    if (!date) {
      return "";
    }

    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    return new Date(Date.UTC(year, month, day, 0, 0, 0, 0)).toISOString();
  }, []);

  const handleChange = useCallback(
    (onFieldChange: (...event: any[]) => void) => {
      return (date: Date | undefined) => {
        if (onChange) {
          onChange(date);
        }
        const formatter = formatDate || defaultFormatDate;
        onFieldChange(formatter(date));
      };
    },
    [formatDate, defaultFormatDate, onChange],
  );

  const datepickerElement = (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange: onFieldChange, value } }) => {
        const handleClear = () => {
          const formatter = formatDate || defaultFormatDate;
          onFieldChange(formatter(undefined));
          if (onChange) {
            onChange(undefined);
          }
        };

        return (
          <DatePicker
            {...restDatepickerProps}
            onChange={handleChange(onFieldChange)}
            value={value ? DateTimeUtils.parseISODateOnly(value) : undefined}
            invalid={invalid || !!errorMessage}
            inputId={inputId}
            onClear={
              restDatepickerProps.onClear || (clear ? handleClear : undefined)
            }
          />
        );
      }}
    />
  );

  return (
    <FormField
      label={label}
      message={message ?? errorMessage}
      description={description}
      required={required}
      tooltipContent={tooltipContent}
      className={formElementClass}
      accessory={accessory}
      htmlFor={inputId}
      color={restDatepickerProps.color}
    >
      {datepickerElement}
    </FormField>
  );
};
